/**
 * StaticGeoDataService
 *
 * Service for loading geographical data from static JSON files
 * and transforming it into SelectOption formats for cascading field dependencies.
 *
 * Data Sources:
 * - /public/data/countries.json: Country data with country_code
 * - /public/data/provinces.json: Province data linked by country_code
 * - /public/data/cities.json: City data linked by country_code and province
 */

import { SelectOption } from '@xanectech/types';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - StaticGeoDataService');

// JSON file interfaces (matching existing structure)
interface CountryData {
  country_name: string;
  country_code: string; // ISO2 code like "ZA", "US"
}

interface ProvinceData {
  country_code: string;
  province: string;
}

interface CityData {
  city: string;
  country_code: string;
  province: string;
}

export class StaticGeoDataService {
  // Cache the loaded data to avoid repeated fetches
  private static countries: CountryData[] | null = null;
  private static provinces: ProvinceData[] | null = null;
  private static cities: CityData[] | null = null;

  /**
   * Load all geographic data from JSON files
   */
  private static async loadAllData(): Promise<void> {
    if (this.countries !== null) return; // Already loaded

    try {
      const [countriesRes, provincesRes, citiesRes] = await Promise.all([
        fetch('/data/countries.json'),
        fetch('/data/provinces.json'),
        fetch('/data/cities.json'),
      ]);

      if (!countriesRes.ok || !provincesRes.ok || !citiesRes.ok) {
        throw new Error('Failed to fetch geographic data');
      }

      const [countries, provinces, cities] = await Promise.all([
        countriesRes.json() as Promise<CountryData[]>,
        provincesRes.json() as Promise<ProvinceData[]>,
        citiesRes.json() as Promise<CityData[]>,
      ]);

      // Cache the data
      this.countries = countries;
      this.provinces = provinces;
      this.cities = cities;
    } catch (error) {
      logger.error('StaticGeoDataService: Error loading data:', {
        error: error instanceof Error ? error.message : String(error),
      });
      // Initialize with empty arrays as fallback
      this.countries = [];
      this.provinces = [];
      this.cities = [];
    }
  }

  /**
   * Get country options as SelectOption[] for dropdowns
   */
  static async getCountryOptions(): Promise<SelectOption[]> {
    await this.loadAllData();

    return (this.countries || []).map((country) => ({
      label: country.country_name,
      value: country.country_code,
      metadata: {
        type: 'country',
      },
    }));
  }

  /**
   * Get province options as SelectOption[] for dropdowns
   * @param countryCode - Optional country code to filter provinces
   */
  static async getProvinceOptions(
    countryCode?: string,
  ): Promise<SelectOption[]> {
    await this.loadAllData();

    let provinces = this.provinces || [];

    // Filter by country if specified
    if (countryCode) {
      provinces = provinces.filter((p) => p.country_code === countryCode);
    }

    return provinces.map((province) => ({
      label: province.province,
      value: province.province.toLowerCase().replace(/\s+/g, '_'), // Normalized value
      dependsOn: 'country',
      dependentValue: province.country_code,
      metadata: {
        type: 'province',
        country_code: province.country_code,
        original_name: province.province,
      },
    }));
  }

  /**
   * Get city options as SelectOption[] for dropdowns
   * @param countryCode - Optional country code to filter cities
   * @param provinceName - Optional province name to filter cities
   */
  static async getCityOptions(
    countryCode?: string,
    provinceName?: string,
  ): Promise<SelectOption[]> {
    await this.loadAllData();

    let cities = this.cities || [];

    // Filter by country if specified
    if (countryCode) {
      cities = cities.filter((c) => c.country_code === countryCode);
    }

    // Filter by province if specified (support both original and normalized names)
    if (provinceName) {
      const normalizedInput = provinceName.toLowerCase().replace(/\s+/g, '_');
      cities = cities.filter((c) => {
        const normalizedProvince = c.province
          .toLowerCase()
          .replace(/\s+/g, '_');
        return (
          c.province === provinceName || normalizedProvince === normalizedInput
        );
      });
    }

    return cities.map((city) => ({
      label: city.city,
      value: city.city.toLowerCase().replace(/\s+/g, '_'), // Normalized value
      dependsOn: provinceName ? 'province' : 'country',
      dependentValue: provinceName || countryCode,
      metadata: {
        type: 'city',
        country_code: city.country_code,
        province: city.province,
        original_name: city.city,
      },
    }));
  }

  /**
   * Generate all hierarchical options with proper dependencies
   */
  static async generateHierarchicalOptions(): Promise<{
    countries: SelectOption[];
    provinces: SelectOption[];
    cities: SelectOption[];
  }> {
    const [countries, provinces, cities] = await Promise.all([
      this.getCountryOptions(),
      this.getProvinceOptions(),
      this.getCityOptions(),
    ]);

    return { countries, provinces, cities };
  }

  /**
   * Get options for a specific cascade level
   * @param level - The cascade level needed
   * @param parentValues - Values from parent fields for filtering
   */
  static async getOptionsForLevel(
    level: 'country' | 'province' | 'city',
    parentValues: { country?: string; province?: string } = {},
  ): Promise<SelectOption[]> {
    switch (level) {
      case 'country':
        return this.getCountryOptions();
      case 'province':
        return this.getProvinceOptions(parentValues.country);
      case 'city':
        return this.getCityOptions(parentValues.country, parentValues.province);
      default:
        return [];
    }
  }

  /**
   * Utility to determine what cascade levels are needed
   */
  static getCascadeLevels(requiresProvince: boolean, requiresCity: boolean) {
    const levels: Array<'country' | 'province' | 'city'> = ['country'];

    if (requiresProvince || requiresCity) {
      levels.push('province');
    }

    if (requiresCity) {
      levels.push('city');
    }

    return levels;
  }

  /**
   * Clear cached data (useful for testing or forcing refresh)
   */
  static clearCache(): void {
    this.countries = null;
    this.provinces = null;
    this.cities = null;
  }

  /**
   * Check if data is loaded
   */
  static isDataLoaded(): boolean {
    return this.countries !== null;
  }

  /**
   * Get data statistics
   */
  static async getDataStats() {
    await this.loadAllData();
    return {
      countries: this.countries?.length || 0,
      provinces: this.provinces?.length || 0,
      cities: this.cities?.length || 0,
      isLoaded: this.isDataLoaded(),
    };
  }
}
